import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import agentUrls from "../config/agentUrls";

import {
  FaMicrophone,
  FaWhatsapp,
  FaInstagram,
  FaUsers,
  FaHeadset,
  FaShoppingCart,
  FaChartLine,
  FaHeartbeat,
  FaMoneyBillWave,
  FaBalanceScale,
  FaBullhorn,
  FaCogs,
  FaHome,
  FaSearch,
  FaStar
} from "react-icons/fa";

/* ================= AGENTS DATA ================= */

const agents = [
  {
    name: "Voice Agent",
    url: agentUrls.voice,
    industry: "Operations",
    desc: "AI-powered voice agent for automated calls and conversations.",
    tier: "Enterprise",
    rating: 4.5,
    icon: <FaMicrophone />
  },
  {
    name: "WhatsApp Agent",
    url: agentUrls.whatsapp,
    industry: "Customer Support",
    desc: "Automate WhatsApp chats, lead qualification, and support.",
    tier: "Enterprise",
    rating: 4.6,
    icon: <FaWhatsapp />
  },
  {
    name: "Instagram Agent",
    url: agentUrls.instagram,
    industry: "Marketing",
    desc: "Automate DMs, generate leads, and boost engagement.",
    tier: "Enterprise",
    rating: 4.7,
    icon: <FaInstagram />
  },
  {
    name: "Interview Agent",
    url: agentUrls.interview,
    industry: "HR",
    desc: "Prepare candidates, organize interview sessions, and streamline hiring workflows.",
    tier: "Enterprise",
    rating: 4.5,
    icon: <FaUsers />
  },
  {
    name: "Marketing Agent",
    url: agentUrls.marketing,
    industry: "Marketing",
    desc: "Coordinate marketing strategy, content, SEO, social media, ads, email, and research workflows.",
    tier: "Enterprise",
    rating: 4.7,
    icon: <FaBullhorn />
  },
  {
    name: "Finance Agent",
    url: agentUrls.finance,
    industry: "Finance",
    desc: "Analyze financial performance, cash flow, expenses, forecasts, reconciliation, and reporting.",
    tier: "Enterprise",
    rating: 4.7,
    icon: <FaMoneyBillWave />
  },
  {
    name: "HR Agent",
    url: agentUrls.hr,
    industry: "HR",
    desc: "Manage employees, onboarding, attendance, leave, payroll, documents, benefits, and HR support.",
    tier: "Enterprise",
    rating: 4.6,
    icon: <FaUsers />
  },
  {
    name: "Banking Agent",
    url: agentUrls.banking,
    industry: "Finance",
    desc: "Work with accounts, transactions, beneficiaries, loans, statements, fraud, risk, and audit workflows.",
    tier: "Enterprise",
    rating: 4.6,
    icon: <FaBalanceScale />
  }
];

/* ================= INDUSTRY ICONS ================= */

const industryIcons = {
  Sales: <FaChartLine />,
  Ecommerce: <FaShoppingCart />,
  Healthcare: <FaHeartbeat />,
  HR: <FaUsers />,
  Finance: <FaMoneyBillWave />,
  Legal: <FaBalanceScale />,
  Marketing: <FaBullhorn />,
  Operations: <FaCogs />,
  "Real Estate": <FaHome />,
  "Customer Support": <FaHeadset />
};

/* ================= COMPONENT ================= */

function MarketplaceAgents() {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();


  const agentsPerPage = 9;

  /* SEARCH FILTER */
  const filtered = agents.filter((agent) =>
    agent.name.toLowerCase().includes(search.toLowerCase()) ||
    agent.industry.toLowerCase().includes(search.toLowerCase())
  );

  /* PAGINATION */
  const indexLast = currentPage * agentsPerPage;
  const indexFirst = indexLast - agentsPerPage;
  const currentAgents = filtered.slice(indexFirst, indexLast);
  const totalPages = Math.ceil(filtered.length / agentsPerPage);

  return (
    <section className="marketplace-section">

      {/* SEARCH */}
      <div className="search-box">
        <FaSearch />
        <input
          type="text"
          placeholder="Search Agents..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* AGENTS GRID */}
      <div className="container">
        <div className="row agents-grid">

          {currentAgents.map((agent, i) => (
            <div className="col-12 col-md-6 col-lg-4 d-flex" key={i}>

              <div
                className="agent-card w-100"
                onClick={() => { navigate(agent.url); }}
                style={{ cursor: "pointer" }}
              >

                {/* TOP */}
                <div className="agent-top">

                  <div className="agent-icon">
                    {industryIcons[agent.industry] || agent.icon}
                  </div>

                  <div>
                    <h3>{agent.name}</h3>

                    <div className="agent-tags">
                      <span className="tag">{agent.industry}</span>
                      <span className="tier">{agent.tier}</span>
                    </div>
                  </div>

                </div>

                {/* DESCRIPTION */}
                <p className="agent-desc">{agent.desc}</p>

                {/* RATING */}
                <div className="rating">
                  {[...Array(Math.round(agent.rating))].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                  <span>{agent.rating}</span>
                </div>

                {/* BUTTONS */}
                <div className="agent-buttons">

                  <button
                    className="details-btn"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent card click
                      navigate(agent.url);
                    }}
                  >
                    View Details
                  </button>

                 <button
                  className="activate-btn"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent card click
                    window.location.href = "/demo";
                  }}
                >
                  Activate
                </button>

                </div>

              </div>

            </div>
          ))}

        </div>
      </div>

      {/* PAGINATION */}
      {totalPages > 1 && <div className="pagination">

        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Previous
        </button>

        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            className={currentPage === i + 1 ? "active-page" : ""}
            onClick={() => setCurrentPage(i + 1)}
          >
            {i + 1}
          </button>
        ))}

        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Next
        </button>

      </div>}

    </section>
  );
}

export default MarketplaceAgents;